import { Box, Link, Stack } from '@mui/material'
import { ShowMore } from '@re-dev/react-truncate'
import { useIsFeatureEnabled } from '@thriveglobal/thrive-web-core'
import {
    CoreTypography,
    LeafIcon,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import { ReactNode } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { FeatureFlags } from '../../../constants'

export type CardDetailsProps = {
    title?: string | React.ReactNode
    titleVariant?: 'h3' | 'h5'
    subTitle?: string
    subTitleLineClamp?: number
    topLeftContent?: ReactNode
    topRightContent?: ReactNode
    bottomLeftContent?: ReactNode
    bottomRightContent?: ReactNode
    contrastCard?: boolean
    hasViewMoreAction?: boolean
    onViewMore?: () => void
}

const intlMessages = defineMessages({
    viewMore: {
        defaultMessage: 'view more',
        description: 'Text for view more link'
    },
    viewMoreAriaLabel: {
        defaultMessage: 'Opens the popup with revealed info details',
        description: 'Aria label for view more link'
    }
})

export default function CardDetails({
    title,
    titleVariant = 'h5',
    subTitle,
    subTitleLineClamp = 2,
    topLeftContent,
    topRightContent,
    bottomLeftContent,
    bottomRightContent,
    hasViewMoreAction,
    onViewMore
}: CardDetailsProps) {
    const isNewCompanyResourceEnabled = useIsFeatureEnabled(
        FeatureFlags.IsNewCompanyResourceEnabled,
        false,
        true
    )
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const showTopContent = topLeftContent || topRightContent
    const showBottomContent = bottomLeftContent || bottomRightContent

    return (
        <Stack width={'100%'} height={'100%'} gap={2}>
            {showTopContent && (
                <Stack
                    flexWrap="wrap"
                    direction={'row'}
                    sx={{
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack justifyContent="flex-start" alignItems="flex-start">
                        {topLeftContent}
                    </Stack>

                    <Stack justifyContent="flex-start" alignItems="flex-end">
                        {topRightContent}
                    </Stack>
                </Stack>
            )}
            <Stack flex={1}>
                <CoreTypography
                    variant={titleVariant}
                    component={'h3'}
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}
                    data-testid="card-details-title"
                >
                    {title}
                </CoreTypography>
                <Box>
                    {(!isNewCompanyResourceEnabled ||
                        (isNewCompanyResourceEnabled &&
                            !hasViewMoreAction)) && (
                        <CoreTypography
                            variant="body2"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: subTitleLineClamp,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                            data-testid="card-details-subtitle"
                        >
                            {subTitle}
                        </CoreTypography>
                    )}
                    {isNewCompanyResourceEnabled && hasViewMoreAction && (
                        <CoreTypography
                            variant="body1"
                            color={theme.palette.grey[900]}
                            sx={{
                                mt: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: subTitleLineClamp,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                            data-testid="card-details-subtitle"
                        >
                            <ShowMore
                                lines={subTitleLineClamp}
                                more={
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        ...
                                        <Link
                                            aria-label={formatMessage(
                                                intlMessages.viewMoreAriaLabel
                                            )}
                                            tabIndex={0}
                                            variant={'body1'}
                                            color={'secondary.contrastText'}
                                            onClick={(e) => {
                                                if (onViewMore) {
                                                    e.stopPropagation()
                                                    onViewMore()
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' &&
                                                    onViewMore
                                                ) {
                                                    e.stopPropagation()
                                                    onViewMore()
                                                }
                                            }}
                                        >
                                            {formatMessage(
                                                intlMessages.viewMore
                                            )}
                                        </Link>
                                        <LeafIcon
                                            icon={'angle-down'}
                                            fontSize={'small'}
                                        />
                                    </Box>
                                }
                            >
                                {subTitle}
                            </ShowMore>
                        </CoreTypography>
                    )}
                </Box>
            </Stack>
            {showBottomContent && (
                <Stack
                    flexWrap="wrap"
                    direction={'row'}
                    justifyContent="space-between"
                    sx={{
                        width: '100%'
                    }}
                >
                    <Stack justifyContent="flex-end" alignItems="flex-start">
                        {bottomLeftContent}
                    </Stack>

                    <Stack justifyContent="flex-end" alignItems="flex-end">
                        {bottomRightContent}
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}
